import React, { useState, useEffect, useRef } from "react";
import { BASE_URL } from "utils/constant";
import TaskGroupDetailComponent from "./TaskGroupDetailComponent";
import TaskDetailComponent from "./TaskDetailComponent";
import SubtaskDetailComponent from "./SubTaskDetailComponent";
import TimelineView from "./TimelineView";
import KanbanView from "./KanbanView";
import MainTableView from "./MainTableView";
import { taskApi } from "api/tasks/taskApi";
import { useAlert } from "components/UI_components/Alert";
import NewTimelineView from "./NewTimelineview";
import CustomGantt from "./CustomGantt";

/* ───────── helpers ───────── */
const todayISO = () => new Date().toISOString().slice(0, 10);

// Format API datetime (ISO) to dd/MM/yy for headers like: By Name | 21/04/25
const formatDateDDMMYY = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

// Add helper function to calculate priority based on due date
const calculatePriorityFromDueDate = (dueDate) => {
  if (!dueDate) return 2; // Default to medium priority if no due date

  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 1; // Overdue - High priority
  if (diffDays <= 3) return 1; // Due within 3 days - High priority
  if (diffDays <= 7) return 2; // Due within a week - Medium priority
  return 3; // Due in more than a week - Low priority
};

// Add helper function to calculate status and days
const calculateStatusAndDays = (dueDate) => {
  if (!dueDate) return { statusType: "onTime", statusDays: 0 };

  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { statusType: "overdue", statusDays: Math.abs(diffDays) };
  }
  return { statusType: "onTime", statusDays: diffDays };
};

const priorityText = (n) =>
  ({ 3: "High", 2: "Medium", 1: "Low" }[n] || "Medium");

const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw?.task_groups) return raw.task_groups;
  if (raw?.tasks) return raw.tasks; // subtasks list
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (raw?.data?.results) return raw.data.results;
  return [];
};

const normalizeGroup = (g) => {
  const id = g.task_group_id ?? g.id ?? g.pk;
  const name = g.task_group_name ?? g.group_name ?? g.name ?? "";
  if (!id || !name.trim()) return null;

  // Calculate status percentages based on tasks
  const tasks = g.tasks || [];
  const task_summary = g?.task_group_summary;
  const totalTasks = task_summary?.total_tasks || tasks.length || 0;
  const completedTasks = task_summary?.completed_tasks;
  const inProgressTasks = task_summary?.ongoing_tasks;
  const notStartedTasks = task_summary?.not_started_tasks;
  const overdueTasks = task_summary?.overdue_tasks;

  // Calculate priority based on due date
  const priority = calculatePriorityFromDueDate(g.end_date);
  const highPriority = task_summary?.high_priority || 0;
  const mediumPriority = task_summary?.medium_priority || 0;
  const lowPriority = task_summary?.low_priority || 0;

  // Calculate status and days
  const { statusType, statusDays } = calculateStatusAndDays(g.end_date);

  return {
    id,
    name,
    author: g.created_by || "Unknown",
    assignee: g.assignee || "Unassigned",
    startDate: g.start_date || todayISO(),
    endDate: g.end_date || todayISO(),
    dueDate: g.end_date || "",
    color: "#9c27b0",
    statusPercentages: {
      completed: (completedTasks / totalTasks) * 100,
      inProgress: (inProgressTasks / totalTasks) * 100,
      notStarted: (notStartedTasks / totalTasks) * 100,
      overdue: (overdueTasks / totalTasks) * 100,
    },
    priorityPercentages: {
      high: (highPriority / totalTasks) * 100,
      medium: (mediumPriority / totalTasks) * 100,
      low: (lowPriority / totalTasks) * 100,
    },
    statusLabel: g.status || "Not started",
    priority: priorityText(priority),
    priorityValue: priority,
    statusDays,
    statusType,
    allocated_members: g.allocated_members || [],
    expanded: false,
    tasks: tasks || [],
    approvers: g.approvers || [],
  };
};

function normalizeTask(rawTask, groupId, expandedTasks) {
  const id = rawTask.task_id ?? rawTask.id;
  return {
    id,
    task_group: groupId,
    name: rawTask.task_name ?? rawTask.name ?? "(no-title)",
    status: rawTask.task_status || "Ongoing",
    priority: priorityText(rawTask.priority),
    priorityId: rawTask.priority,
    dueDate: rawTask.end_date || null,
    task_details: rawTask.task_details || "–",
    assignee: rawTask.assignee || "Unassigned",
    start_date: rawTask.start_date || null,
    end_date: rawTask.end_date || null,
    statusType: "overdue",
    statusDays: null,
    created_by: rawTask.created_by || "Unknown",
    createdAt: formatDateDDMMYY(rawTask.created_on),
    members: rawTask.allocated_members || [],
    task_members: rawTask.task_members || [],
    expanded: Boolean(expandedTasks[id]),
    subtasks_summary: rawTask.subtasks_summary,
    subtasks: [],
  };
}

function normalizeSubtask(rawSub, groupId, task) {
  const id = rawSub.subtask_id ?? rawSub.id;

  return {
    id,
    name: rawSub.subtask_name ?? rawSub.name ?? "(no-title)",
    status: rawSub.is_completed
      ? "Completed"
      : task.status === "Not started"
        ? "Not started"
        : new Date(rawSub.start_date) > new Date()
          ? "Not started"
          : new Date(rawSub.end_date) < new Date()
            ? "Overdue"
            : "Ongoing",
    subtask_details: rawSub.subtask_details || "–",
    is_completed: rawSub.is_completed || false,
    dueDate: rawSub.end_date || "",
    start_date: rawSub.start_date || todayISO(),
    end_date: rawSub.end_date || todayISO(),
    completed_on: rawSub.completed_on || null,
    completed_by: rawSub.completed_by || null,
    priority: priorityText(rawSub.priority),
    assignee: rawSub.assign_to || [],
    statusType: "overdue",
    statusDays: 5,
    created_by: rawSub.created_by || "",
    task_group: groupId,
  };
}

/* avatar helpers */
export const getInitials = (n) =>
  !n || n === "Unassigned"
    ? "?"
    : n
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
const getAvatarColor = (n) => {
  const colors = [
    "bg-blue-800",
    "bg-purple-800",
    "bg-green-700",
    "bg-red-700",
    "bg-yellow-700",
    "bg-pink-700",
    "bg-indigo-700",
  ];
  const hash = [...(n || "?")].reduce((s, c) => s + c.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

/* ───────── component ───────── */

const AddTaskGroupModal = ({ handleSubmit, onClose }) => {
  const [taskGroupName, setTaskGroupName] = useState("");
  const [dueDate, setDueDate] = useState(todayISO());

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (taskGroupName.trim()) {
      handleSubmit({
        name: taskGroupName.trim(),
        dueDate: dueDate,
      });
      onClose();
    }
  };

  return (
    <>
      {/* Modal backdrop/overlay */}
      <div
        className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-40"
        onClick={onClose}
      >
        {/* Modal content */}
        <div
          className="bg-white  rounded-lg w-full max-w-md mx-4 z-50"
          onClick={(e) => e.stopPropagation()} // Prevents clicks on modal from closing it
        >
          <div className="px-6 py-4">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Add Task Group
            </h2>

            <form onSubmit={handleFormSubmit}>
              <div className="mb-6">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="taskGroupName"
                >
                  Task Group Name
                </label>
                <input
                  type="text"
                  id="taskGroupName"
                  value={taskGroupName}
                  onChange={(e) => setTaskGroupName(e.target.value)}
                  className=" appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring focus:ring-blue-300 focus:border-blue-500"
                  placeholder="Enter task group name"
                  autoFocus
                />
              </div>

              {/* <div className="mb-6">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="dueDate"
                >
                  Due Date
                </label>
                <input
                  type="date"
                  id="dueDate"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring focus:ring-blue-300 focus:border-blue-500"
                />
              </div> */}

              <div className="flex items-center justify-end space-x-4 mt-6">
                <button
                  className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring focus:ring-gray-300 transition duration-150"
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="bg-btn-bg hover:bg-purple-800 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring focus:ring-blue-300 transition duration-150"
                  type="submit"
                  disabled={!taskGroupName.trim()}
                >
                  Add Task Group
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
const TaskHome = () => {
  const { addAlert } = useAlert();
  // Ref to the scrolling container so we can keep its scroll position stable
  const scrollContainerRef = useRef(null);

  /* state */
  const [taskGroups, setTaskGroups] = useState(() => {
    const cache = JSON.parse(localStorage.getItem("taskGroups") || "[]");
    const expandedGroups = JSON.parse(
      localStorage.getItem("expandedGroups") || "{}"
    );
    const expandedTasks = JSON.parse(
      localStorage.getItem("expandedTasks") || "{}"
    );

    return cache
      .filter((g) => g && g.id && g.name)
      .map((group) => ({
        ...group,
        expanded: expandedGroups[group.id] || false,
        tasks: (group.tasks || []).map((task) => ({
          ...task,
          expanded: expandedTasks[task.id] || false,
        })),
      }));
  });
  const [refreshing, setRefreshing] = useState(false);
  const [showAddTaskGroupModal, setShowAddTaskGroupModal] = useState(false);
  const [taskAuthority, setTaskAuthority] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(null);
  const [selectedTaskGroupId, setSelectedTaskGroupId] = useState(null);
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [parentTaskDueDate, setParentTaskDueDate] = useState(null);
  const [activeView, setActiveView] = useState("Main Table");
  const [selectedTaskGroup, setSelectedTaskGroup] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [openPriorityDropdown, setOpenPriorityDropdown] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState("");
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskName, setEditingSubtaskName] = useState("");
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const statusOptions = [
    { label: "Ongoing", color: "bg-yellow-400 text-gray-900" },
    { label: "Complete", color: "bg-green-500 text-white" },
    { label: "Halted", color: "bg-red-500 text-white" },
    // { label: "Overdue", color: "bg-red-500 text-white" },
    { label: "Not started", color: "bg-gray-400 text-white" },
  ];

  /* auth */
  const siteId = Number(localStorage.getItem("site"));
  const token = localStorage.getItem("access");
  const isOwner = localStorage.getItem("isOwner") === "true";
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const statePersistentRefresh = async () => {
    setRefreshing(true);
    try {
      // 1. Fetch groups
      const res = await fetch(
        `${BASE_URL}/task/get-all-task-groups/?site_id=${siteId}`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error(res.statusText);
      let groups = toArray(await res.json())
        .map(normalizeGroup)
        .filter(Boolean);

      // 2. Read expansion state
      const expandedGroups = JSON.parse(
        localStorage.getItem("expandedGroups") || "{}"
      );
      const expandedTasks = JSON.parse(
        localStorage.getItem("expandedTasks") || "{}"
      );

      // 3. Mark which groups are expanded
      groups.forEach((g) => {
        g.expanded = Boolean(expandedGroups[g.id]);
      });

      // 4. Parallel fetch tasks for all expanded groups
      await Promise.all(
        groups
          .filter((g) => g.expanded)
          .map(async (group) => {
            const rawTasks = await taskApi.getAllTasks(group.id);
            group.tasks = rawTasks.map((t) =>
              normalizeTask(t, group.id, expandedTasks)
            );
          })
      );

      // 5. Parallel fetch subtasks for all expanded tasks
      const subtaskPromises = [];
      groups.forEach((group) => {
        (group.tasks.filter((t) => t.expanded) || []).forEach((task) => {
          subtaskPromises.push(
            taskApi.getAllSubtasks(task.id).then((rawSubs) => {
              task.subtasks = rawSubs.map((st) =>
                normalizeSubtask(st, group.id, task)
              );
            })
          );
        });
      });
      await Promise.all(subtaskPromises);
      // 6. Update state
      setTaskGroups(groups);
    } catch (error) {
      console.warn("Error refreshing task groups:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Refresh task groups when children request a refetch (e.g., after delete)
  useEffect(() => {
    statePersistentRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch site members to determine if current user has task authority
  useEffect(() => {
    const fetchSiteMembersForAuthority = async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData"));
        if (!siteData?.id) return;
        const response = await taskApi.getSiteMembers(siteData.id);
        if (response?.status) {
          const user = JSON.parse(localStorage.getItem("user"));
          const currentMember = response.data.find(
            (member) => member.user_id === user.user
          );
          setTaskAuthority(
            currentMember?.create_taskgroup ||
            currentMember?.is_admin ||
            currentMember?.joined_as === "Owner"
          );
        }
      } catch (e) {
        // ignore errors silently
      }
    };

    fetchSiteMembersForAuthority();
  }, []);

  /* mount: get groups */
  // useEffect(() => {
  //   setRefreshing(true);
  //   (async () => {
  //     try {
  //       const res = await fetch(
  //         `${BASE_URL}/task/get-all-task-groups/?site_id=${siteId}`,
  //         {
  //           headers: authHeaders,
  //         }
  //       );
  //       if (!res.ok) throw new Error();
  //       const groups = toArray(await res.json())
  //         .map(normalizeGroup)
  //         .filter(Boolean);
  //       setTaskGroups(groups);
  //     } catch {
  //       console.warn("Using cached taskGroups");
  //     } finally {
  //       setRefreshing(false);
  //     }
  //   })();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  /* persist */
  useEffect(() => {
    localStorage.setItem("taskGroups", JSON.stringify(taskGroups));
  }, [taskGroups]);

  useEffect(() => {
    const expandedGroups = {};
    const expandedTasks = {};

    taskGroups.forEach((group) => {
      if (group.expanded) {
        expandedGroups[group.id] = true;
      }
      group.tasks?.forEach((task) => {
        if (task.expanded) {
          expandedTasks[task.id] = true;
        }
      });
    });

    localStorage.setItem("expandedGroups", JSON.stringify(expandedGroups));
    localStorage.setItem("expandedTasks", JSON.stringify(expandedTasks));
  }, [taskGroups]);

  /* ---------- API helpers ---------- */
  const fetchTasksForGroup = async (tgId) => {
    try {
      const res = await fetch(`${BASE_URL}/task/get-all-tasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ task_group: tgId }),
      });
      if (!res.ok) throw new Error();
      const list = toArray(await res.json());

      // Build normalized tasks
      const normalizedTasks = list.map((t) => ({
        id: t.task_id ?? t.id,
        task_group: tgId,
        name: t.task_name ?? t.name ?? "(no-title)",
        status: t.task_status || "Ongoing",
        priority: priorityText(t.priority),
        priorityId: t.priority,
        dueDate: t.end_date || "",
        task_details: t.task_details || "–",
        assignee: t.assignee || "Unassigned",
        start_date: t.start_date || todayISO(),
        end_date: t.end_date || todayISO(),
        statusType: "overdue",
        statusDays: 20,
        created_by: t.created_by || "Unknown",
        createdAt: formatDateDDMMYY(t.created_on),
        members: t.allocated_members || [],
        task_members: t.task_members || [],
        expanded: false,
        subtasks_summary: t.subtasks_summary,
        subtasks: [],
      }));

      // Recompute group's allocated members from tasks to reflect latest assignees
      const allocatedMembersMap = new Map();
      list.forEach((t) => {
        (t.allocated_members || []).forEach((m) => {
          if (m?.member_id != null && !allocatedMembersMap.has(m.member_id)) {
            allocatedMembersMap.set(m.member_id, m);
          }
        });
      });
      const recomputedAllocatedMembers = Array.from(allocatedMembersMap.values());

      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: normalizedTasks,
              // Always replace with recomputed members; if none remain, show empty list
              allocated_members: recomputedAllocatedMembers,
            }
            : tg
        )
      );
    } catch {
      console.error("Couldn't load tasks");
    }
  };

  const fetchSubtasksForTask = async (tgId, taskId) => {
    try {
      const res = await fetch(`${BASE_URL}/task/get-all-subtasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ task_id: taskId }),
      });
      if (!res.ok) throw new Error();
      const list = toArray(await res.json());
      const subs = list.map((st) => ({
        id: st.subtask_id ?? st.id,
        name: st.subtask_name ?? st.name ?? "(no-title)",
        status: st.is_completed
          ? "Completed"
          : new Date(st.start_date) > new Date()
            ? "Not started"
            : new Date(st.end_date) < new Date()
              ? "Overdue"
              : "Ongoing",
        subtask_details: st.subtask_details || "–",
        is_completed: st.is_completed || false,
        completed_on: st.completed_on || null,
        completed_by: st.completed_by || null,
        dueDate: st.end_date || "",
        start_date: st.start_date || todayISO(),
        end_date: st.end_date || todayISO(),
        priority: priorityText(st?.priority),
        assignee: st.assign_to || [],
        statusType: "overdue",
        task_group: tgId,
        statusDays: 5,
        created_by: st.created_by || "",
      }));
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: tg.tasks.map((t) =>
                t.id === taskId ? { ...t, subtasks: subs } : t
              ),
            }
            : tg
        )
      );
    } catch {
      console.error("Couldn't load subtasks");
    }
  };

  /* ---------- toggles ---------- */
  const toggleTaskGroup = (e, id) => {
    e.stopPropagation();

    // Capture current scroll position of the container (if mounted)
    const currentScrollTop = scrollContainerRef.current?.scrollTop ?? 0;

    // Toggle the group
    setTaskGroups((prev) =>
      prev.map((tg) => (tg.id === id ? { ...tg, expanded: !tg.expanded } : tg))
    );

    // After DOM updates, restore the scroll position so the view doesn't jump
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = currentScrollTop;
      }
    });

    const tg = taskGroups.find((g) => g.id === id);
    if (tg && !tg.expanded && tg.tasks.length === 0) fetchTasksForGroup(id);
  };

  const toggleTask = (e, tgId, taskId) => {
    e.stopPropagation();

    // Capture current scroll position of the container
    const currentScrollTop = scrollContainerRef.current?.scrollTop ?? 0;

    // Toggle the task expanded state
    setTaskGroups((prev) =>
      prev.map((tg) =>
        tg.id === tgId
          ? {
            ...tg,
            tasks: tg.tasks.map((t) =>
              t.id === taskId ? { ...t, expanded: !t.expanded } : t
            ),
          }
          : tg
      )
    );

    // Restore scroll position after DOM updates to prevent jumping
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = currentScrollTop;
      }
    });

    const tg = taskGroups.find((g) => g.id === tgId);
    const tk = tg?.tasks.find((t) => t.id === taskId);
    if (tk && !tk.expanded && tk.subtasks.length === 0) {
      fetchSubtasksForTask(tgId, taskId);
    }
  };

  // Helper to get status proportions for a group
  const getTaskGroupStatusProportions = (tasks) => {
    const total = tasks.length || 1;
    const counts = { Ongoing: 0, Complete: 0, Overdue: 0, "Not started": 0 };
    tasks.forEach((t) => {
      if (counts[t.status]) {
        counts[t.status]++;
      } else {
        counts["Not started"]++;
      }
    });
    return {
      Ongoing: (counts.Ongoing / total) * 100,
      Complete: (counts.Complete / total) * 100,
      Overdue: (counts.Overdue / total) * 100,
      NotStarted: (counts["Not started"] / total) * 100,
    };
  };

  const handleTaskStatusChange = async (tgId, taskId, newStatus) => {
    // Update the task status in the backend
    try {
      const task = taskGroups
        .filter((tg) => tg.id === tgId)[0]
        .tasks.filter((task) => task.id === taskId)[0];

      // console.log("selected task", task);
      const taskData = {
        task_name: task.name,
        task_status: newStatus,
        task_details: task.task_details,
        priority: task.priorityId,
        start_date: task.start_date,
        end_date: task.end_date,
        estimated_amount: task?.estimated_amount || 0,
        task_group: task.task_group,
      };

      const res = await taskApi.updateTask(task.id, taskData);
      if (res?.detail) {
        addAlert(
          "You do not have permission to update this task status.",
          "error"
        );
        setOpenStatusDropdown(null);
        return;
      }
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: tg.tasks.map((task) =>
                task.id === taskId
                  ? {
                    ...task,
                    status: newStatus,
                    subtasks: task.subtasks.map((subtask) => ({
                      ...subtask,
                      status:
                        subtask.is_completed
                          ? "Completed"
                          : newStatus === "Not started"
                            ? "Not started"
                            : new Date(subtask?.end_date) < new Date()
                              ? "Overdue"
                              : new Date(subtask?.start_date) > new Date()
                                ? "Not started"
                                : "Ongoing"
                    })),
                  }
                  : task
              ),
            }
            : tg
        )
      );

      // if (!res.ok) {
      //   throw new Error("Failed to update task status");
      // }
    } catch (error) {
      console.error("Error updating task status:", error);

      addAlert("Failed to update task status. Please try again.", "error");
    }

    setOpenStatusDropdown(null);
  };

  const handleTaskPriorityChange = async (tgId, taskId, newPriority) => {
    const task = taskGroups
      .filter((tg) => tg.id === tgId)[0]
      .tasks.filter((task) => task.id === taskId)[0];

    const taskData = {
      task_name: task.name,
      task_status: task.status,
      task_details: task.task_details,
      priority: newPriority,
      start_date: task.start_date,
      end_date: task.end_date,
      estimated_amount: task?.estimated_amount || 0,
      task_group: task.task_group,
    };
    // Update the task priority in the backend
    try {
      const res = await taskApi.updateTask(task.id, taskData);
      if (res?.detail) {
        addAlert(
          "You do not have permission to update this task priority.",
          "error"
        );
        setOpenPriorityDropdown(null);
        return;
      }
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: tg.tasks.map((task) =>
                task.id === taskId
                  ? {
                    ...task,
                    priority: priorityText(newPriority),
                    priorityId: newPriority,
                  }
                  : task
              ),
            }
            : tg
        )
      );
    } catch (error) {
      console.error("Error updating task priority:", error);
      addAlert("Failed to update task priority. Please try again.", "error");
    }

    fetchTasksForGroup(tgId);

    setOpenPriorityDropdown(null);
  };
  /* ---------- API helpers ---------- */
  const addTaskGroup = async (taskData) => {
    if (!taskData.name) return;
    try {
      const res = await fetch(`${BASE_URL}/task/create-task-group/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          site_id: siteId,
          task_group_name: taskData.name.trim(),
          status: "Active",
          start_date: todayISO(),
          end_date: taskData.dueDate || todayISO(),
          priority: 3,
        }),
      });
      if (!res.ok) throw new Error();
      const newG = normalizeGroup((await res.json()).data);
      if (newG) setTaskGroups((prev) => [...prev, newG]);

      addAlert(`Created new task group ${taskData.name}`, "success");
    } catch {
      addAlert("Could not create task group.", "error");
    }
  };

  const addTask = async (taskData) => {
    // const name = window.prompt("Enter task name");
    if (!taskData.name) return;
    try {
      const res = await fetch(`${BASE_URL}/task/create-tasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          task_group: taskData.tgId,
          task_name: taskData.name.trim(),
          task_details: taskData.task_details || "–",
          task_status: "Not Started",
          priority: 1,
          estimated_amount: taskData.estimated_amount || "0",
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === false) throw new Error();
      const tid = data.data?.task_id ?? data.task_id;
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === taskData.tgId
            ? {
              ...tg,
              tasks: [
                ...tg.tasks,
                {
                  id: tid,
                  name: taskData?.name,
                  task_group: taskData.tgId,
                  status: taskData?.task_status || "Not started",
                  priority: "Low",
                  dueDate: data.data?.end_date || null,
                  assignee: taskData?.assignee || [],
                  members: taskData?.allocated_members || [],
                  statusType: "overdue",
                  statusDays: 20,
                  expanded: false,
                  created_by: data.data?.created_by,
                  createdAt: todayISO(),
                  subtasks: [],
                },
              ],
            }
            : tg
        )
      );
      addAlert(`Successfully created task ${taskData.name}`, "success");
    } catch {
      addAlert("Could not create task.", "error");
    }
  };

  const addSubtask = async (subtaskData) => {
    if (!subtaskData.subtask_name) return;
    try {
      const tgId = selectedTaskGroupId;
      const taskId = selectedTaskId;
      const res = await fetch(`${BASE_URL}/task/create-subtasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          task_id: subtaskData.task_id,
          subtask_name: subtaskData.subtask_name.trim(),
          subtask_details: subtaskData.subtask_details || null,
          assign_to: subtaskData.assign_to || [],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === false) throw new Error();
      const sid = data.data?.id ?? data.id;
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: tg.tasks.map((t) =>
                t.id === taskId
                  ? {
                    ...t,
                    subtasks: [
                      ...t.subtasks,
                      {
                        id: sid,
                        name: subtaskData.subtask_name,
                        status: "Not started",
                        priority:
                          priorityText(subtaskData.priority) || "Medium",
                        assignee: subtaskData.assign_to || [],
                        statusType: "overdue",
                        statusDays: 5,
                      },
                    ],
                  }
                  : t
              ),
            }
            : tg
        )
      );

      // fetchSubtasksForTask(tgId, taskId); // Refresh subtasks for the task
      addAlert(
        `Successfully created subtask ${subtaskData.subtask_name}`,
        "success"
      );
    } catch {
      addAlert("Could not create subtask.", "error");
    }
  };

  /* ---------- toggles ---------- */
  const handleAssignApprovers = async (approvers) => {
    try {
      // Implementation for assigning approvers
      // console.log("Assigning approvers:", approvers);
      // Add your API call here if needed
    } catch (error) {
      console.error("Error assigning approvers:", error);
    }
  };

  const handleAddTaskUpdate = async (updateData) => {
    try {
      // Implementation for adding task update
      // console.log("Adding task update:", updateData);
      // Add your API call here if needed
    } catch (error) {
      console.error("Error adding task update:", error);
    }
  };
  const handleAddSubtaskClick = (tgId, taskId, dueDate) => {
    setSelectedTaskGroupId(tgId);
    setSelectedTaskId(taskId);
    setParentTaskDueDate(dueDate);
    setShowAddSubtaskModal(taskId);
  };

  const deleteSubtask = async (tgId, taskId, subtaskId) => {
    try {
      const res = await fetch(`${BASE_URL}/task/delete-subtask/${subtaskId}/`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error("Failed to delete subtask");
      }

      // Update local state to remove the deleted subtask
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: tg.tasks.map((t) =>
                t.id === taskId
                  ? {
                    ...t,
                    subtasks: t.subtasks.filter(
                      (st) => st.id !== subtaskId
                    ),
                  }
                  : t
              ),
            }
            : tg
        )
      );
    } catch (error) {
      console.error("Error deleting subtask:", error);
      addAlert("Failed to delete subtask. Please try again.", "error");
    }
  };

  // Add this new function to handle the edit start
  const handleEditStart = (group) => {
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
  };

  // Add this new function to handle the edit cancel
  const handleEditCancel = () => {
    setEditingGroupId(null);
    setEditingGroupName("");
  };

  // Add this new function to handle the edit submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingGroupName.trim()) {
      addAlert("Task group name cannot be empty.", "error");
      return;
    }

    try {
      const res = await fetch(
        `${BASE_URL}/task/task-group/${editingGroupId}/update`,
        {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify({
            task_group_name: editingGroupName,
          }),
        }
      );

      const data = await res.json();
      if (data?.detail) {
        addAlert(
          "You do not have permission to update this task group name.",
          "error"
        );
        setEditingGroupId(null);
        return;
      }

      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === editingGroupId
            ? { ...tg, name: editingGroupName.trim() }
            : tg
        )
      );
      setEditingGroupId(null);
    } catch (e) {
      console.error("update name failed", e);
      // addAlert("Could not update dates.");
    }
  };

  // Add functions for task name editing
  const handleTaskEditStart = (task) => {
    setEditingTaskId(task.id);
    setEditingTaskName(task.name);
  };

  const handleTaskEditCancel = () => {
    setEditingTaskId(null);
    setEditingTaskName("");
  };

  const handleTaskEditSubmit = async (taskName) => {
    const task = taskGroups.filter((tg) =>
      tg.tasks.map((t) => t.id === editingTaskId)
    )[0];

    const taskData = {
      task_name: taskName.trim(),
      task_status: task.status,
      task_details: task.task_details,
      priority: task.priorityId,
      start_date: task.start_date,
      end_date: task.end_date,
      estimated_amount: task?.estimated_amount || 0,
      task_group: task.task_group,
    };

    // Update the task name in the backend
    const res = await taskApi.updateTask(editingTaskId, taskData);
    if (res?.detail) {
      addAlert(
        "You do not have permission to update this task name.You must have Task Authority permission",
        "error"
      );
      setEditingTaskId(null);
      return;
    }

    if (editingTaskName.trim()) {
      setTaskGroups((prev) =>
        prev.map((tg) => ({
          ...tg,
          tasks: tg.tasks.map((t) =>
            t.id === editingTaskId ? { ...t, name: editingTaskName.trim() } : t
          ),
        }))
      );

      setEditingTaskId(null);
      statePersistentRefresh();
    }
  };

  const handleTaskDateChange = async (taskId, newStartDate, newEndDate) => {
    const task = taskGroups
      ?.filter((tg) => tg?.tasks?.some((t) => t.id === taskId))[0]
      ?.tasks?.find((t) => t.id === taskId);
    if (!task) return;
    const taskData = {
      task_name: task.name,
      task_status: task.status,
      task_details: task.task_details,
      priority: task.priorityId,
      start_date: newStartDate,
      end_date: newEndDate,
      estimated_amount: task?.estimated_amount || 0,
      task_group: task.task_group,
    };
    // Update the task dates in the backend
    try {
      const res = await taskApi.updateTask(taskId, taskData);
      if (res?.detail) {
        addAlert(
          "You do not have permission to update this task dates.",
          "error"
        );
        return;
      }
      statePersistentRefresh(); // Refresh the task groups to reflect the changes
      addAlert("Task dates updated successfully.", "success");
      // setTaskGroups((prev) =>
      //   prev.map((tg) => ({
      //     ...tg,
      //     tasks: tg.tasks.map((t) =>
      //       t.id === taskId
      //         ? {
      //             ...t,
      //             start_date: newStartDate,
      //             end_date: newEndDate,
      //           }
      //         : t
      //     ),
      //   }))
      // );
    } catch (error) {
      console.error("Error updating task dates:", error);
      addAlert("Failed to update task dates. Please try again.", "error");
    }
  };

  const handleSubtaskDateChange = async (
    subtaskId,
    newStartDate,
    newEndDate
  ) => {
    // Find the subtask
    const subtask = taskGroups
      .flatMap((tg) => tg.tasks)
      .flatMap((t) => t.subtasks)
      .find((st) => st.id === subtaskId);
    if (!subtask) return;
    // Prepare the data to update
    const subtaskData = {
      subtask_name: subtask.name,
      subtask_details: subtask.subtask_details,
      end_date: newEndDate,
      start_date: newStartDate,
      is_completed: subtask.is_completed,
      // assign_to: subtask.assignee,
    };

    // Update the subtask dates in the backend
    try {
      const res = await taskApi.updateSubtask(subtaskId, subtaskData);
      if (res?.detail) {
        addAlert(
          "You do not have permission to update this subtask dates.",
          "error"
        );
        return;
      }
      // Refresh the task groups to reflect the changes
      statePersistentRefresh();
      addAlert("Subtask dates updated successfully.", "success");
    } catch (error) {
      console.error("Error updating subtask dates:", error);
      addAlert("Failed to update subtask dates. Please try again.", "error");
    }
  };

  // Add functions for subtask name editing
  const handleSubtaskEditStart = (subtask) => {
    setEditingSubtaskId(subtask.id);
    setEditingSubtaskName(subtask.name);
  };

  const handleSubtaskEditCancel = () => {
    setEditingSubtaskId(null);
    setEditingSubtaskName("");
  };

  const handleSubtaskEditSubmit = async (subtaskName) => {
    if (subtaskName.trim()) {
      setTaskGroups((prev) =>
        prev.map((tg) => ({
          ...tg,
          tasks: tg.tasks.map((t) => ({
            ...t,
            subtasks: t.subtasks.map((st) =>
              st.id === editingSubtaskId
                ? { ...st, name: subtaskName.trim() }
                : st
            ),
          })),
        }))
      );

      // find subtask in taskGroups
      const subtask = taskGroups
        .filter((tg) =>
          tg.tasks.some((t) =>
            t.subtasks.some((st) => st.id === editingSubtaskId)
          )
        )[0]
        .tasks.flatMap((t) => t.subtasks)
        .find((st) => st.id === editingSubtaskId);


      const res = taskApi.updateSubtask(editingSubtaskId, {
        subtask_name: subtaskName.trim(),
        subtask_details: subtask.subtask_details,
        end_date: subtask.end_date,
        start_date: subtask.start_date,
        is_completed: subtask.is_completed,
        // assign_to: subtask.assignee,
      });

      setEditingSubtaskId(null);
    }
  };
  const toggleSubtaskComplete = async (tgId, taskId, subId, isChecked) => {
    // Update the subtask completion status in the backend first
    try {
      const taskGroup = taskGroups.find((tg) => tg.id === tgId);
      const task = taskGroup?.tasks.find((task) => task.id === taskId);
      const subtask = task?.subtasks.find((st) => st.id === subId);

      if (!subtask || !task) {
        console.error("Subtask or task not found");
        return;
      }

      // Update subtask in backend
      const subtaskRes = await taskApi.updateSubtask(subId, {
        subtask_name: subtask.name,
        subtask_details: subtask.subtask_details,
        end_date: subtask.end_date,
        start_date: subtask.start_date,
        is_completed: isChecked,
      });

      if (subtaskRes?.detail) {
        addAlert("You do not have permission to update this subtask.", "error");
        return;
      }

      // Calculate what the new task status should be
      const updatedSubtasks = task.subtasks.map((st) =>
        st.id === subId
          ? {
            ...st,
            status: isChecked ? "Completed" : "Ongoing",
            is_completed: isChecked,
          }
          : st
      );

      const allSubtasksCompleted =
        updatedSubtasks.length > 0 &&
        updatedSubtasks.every((st) => st.status === "Completed");

      const newTaskStatus = allSubtasksCompleted ? "Complete" : "Ongoing";

      // If task status needs to change, update it in the backend too
      if (task.status !== newTaskStatus) {
        const taskData = {
          task_name: task.name,
          task_status: newTaskStatus,
          task_details: task.task_details,
          priority: task.priorityId,
          start_date: task.start_date,
          end_date: task.end_date,
          estimated_amount: task?.estimated_amount || 0,
          task_group: task.task_group,
        };

        const taskRes = await taskApi.updateTask(task.id, taskData);
        if (taskRes?.detail) {
          addAlert(
            "You do not have permission to update this task status.",
            "error"
          );
          return;
        }
      }

      // Update local state
      setTaskGroups((prev) =>
        prev.map((tg) =>
          tg.id === tgId
            ? {
              ...tg,
              tasks: tg.tasks.map((task) => {
                if (task.id !== taskId) return task;

                return {
                  ...task,
                  subtasks: updatedSubtasks,
                  status: newTaskStatus,
                };
              }),
            }
            : tg
        )
      );
    } catch (error) {
      console.error("Error updating subtask completion:", error);
      addAlert("Failed to update subtask. Please try again.", "error");
    }
  };

  // Merge supplied members into a group's allocated_members (keeps unique by member_id)
  const mergeGroupAllocatedMembers = (tgId, members) => {
    if (!Array.isArray(members) || members.length === 0) return;
    setTaskGroups((prev) =>
      prev.map((tg) => {
        if (tg.id !== tgId) return tg;
        const existingMap = new Map(
          (tg.allocated_members || []).map((m) => [m?.member_id, m])
        );
        members.forEach((m) => {
          if (m?.member_id != null && !existingMap.has(m.member_id)) {
            existingMap.set(m.member_id, m);
          }
        });
        return { ...tg, allocated_members: Array.from(existingMap.values()) };
      })
    );
  };

  /* -------- helper to preserve scroll when setting "selected" side-panels -------- */
  const preserveScrollSet = (setter) => {
    return (value) => {
      const currentScrollTop = scrollContainerRef.current?.scrollTop ?? 0;
      setter(value);
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = currentScrollTop;
        }
      });
    };
  };

  // Wrapped setters that keep the list from jumping when a detail panel opens
  const handleSetSelectedTaskGroup = preserveScrollSet(setSelectedTaskGroup);
  const handleSetSelectedTask = preserveScrollSet(setSelectedTask);
  const handleSetSelectedSubtask = preserveScrollSet(setSelectedSubtask);

  return (
    <div className="bg-[#F0F2F5] h-full">
      <div className="flex items-center justify-between p-2 border-b">
        <div className="flex items-center space-x-4">
          <button
            className={`px-4 py-2 ${activeView === "Main Table"
              ? "text-purple-700 border-b-2 border-purple-700"
              : "text-gray-500 hover:text-gray-700"
              }`}
            onClick={() => setActiveView("Main Table")}
          >
            Main Table
          </button>
          <button
            className={`px-4 py-2 ${activeView === "Timeline"
              ? "text-purple-700 border-b-2 border-purple-700"
              : "text-gray-500 hover:text-gray-700"
              }`}
            onClick={() => setActiveView("Timeline")}
          >
            Timeline
          </button>
          <button
            className={`px-4 py-2 ${activeView === "Kanban"
              ? "text-purple-700 border-b-2 border-purple-700"
              : "text-gray-500 hover:text-gray-700"
              }`}
            onClick={() => setActiveView("Kanban")}
          >
            Kanban
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 h-[84vh] overflow-y-scroll"
        style={{
          scrollbarWidth: "none",
        }}
      >
        {/* Timeline View - Always rendered, conditionally shown */}
        {/* <div className={activeView === "Timeline" ? "block" : "hidden"}>
          <TimelineView taskGroups={taskGroups} />
        </div> */}

        {/* {activeView === "Timeline" && (
          <NewTimelineView taskGroups={taskGroups} />
        )} */}

        {activeView === "Timeline" && <CustomGantt />}
        {/* Kanban View - render only when active */}
        {activeView === "Kanban" && (
          <KanbanView
            taskGroups={taskGroups}
            refetchTasks={fetchTasksForGroup}
            onTaskStatusChange={handleTaskStatusChange}
            getInitials={getInitials}
            getAvatarColor={getAvatarColor}
          />
        )}

        {/* Main Table View - Always rendered, conditionally shown */}
        <div className={activeView === "Main Table" ? "block" : "hidden"}>
          <MainTableView
            taskGroups={taskGroups}
            refetchTasks={fetchTasksForGroup}
            refetchSubtasks={async (tgId, taskId) => {
              await fetchSubtasksForTask(tgId, taskId);
            }}
            mergeGroupAllocatedMembers={mergeGroupAllocatedMembers}
            onAddTaskGroupClick={() => {
              if (isOwner) setShowAddTaskGroupModal(true);
            }}
            onAddTaskClick={(tgId) => {
              setSelectedTaskGroupId(tgId);

              setShowAddTaskModal(tgId);
            }}
            handleAddTask={addTask}
            showAddTaskModal={showAddTaskModal}
            setShowAddTaskModal={setShowAddTaskModal}
            onToggleGroup={toggleTaskGroup}
            onToggleTask={toggleTask}
            refreshing={refreshing}
            onRefresh={() => {
              statePersistentRefresh();
            }}
            onAddSubtaskClick={handleAddSubtaskClick}
            showAddSubtaskModal={showAddSubtaskModal}
            setShowAddSubtaskModal={setShowAddSubtaskModal}
            handleAddSubtask={addSubtask}
            onToggleSubComplete={toggleSubtaskComplete}
            onTaskStatusChange={handleTaskStatusChange}
            openStatusDropdown={openStatusDropdown}
            setOpenStatusDropdown={setOpenStatusDropdown}
            statusOptions={statusOptions}
            getInitials={getInitials}
            onTaskPriorityChange={handleTaskPriorityChange}
            openPriorityDropdown={openPriorityDropdown}
            setOpenPriorityDropdown={setOpenPriorityDropdown}
            getAvatarColor={getAvatarColor}
            getStatusProportions={getTaskGroupStatusProportions}
            /* inline-edit props */
            editingGroupId={editingGroupId}
            editingGroupName={editingGroupName}
            onGroupEditStart={handleEditStart}
            onGroupEditCancel={handleEditCancel}
            onGroupEditSubmit={handleEditSubmit}
            editingTaskId={editingTaskId}
            editingTaskName={editingTaskName}
            onTaskEditStart={handleTaskEditStart}
            onTaskEditCancel={handleTaskEditCancel}
            onTaskEditSubmit={handleTaskEditSubmit}
            editingSubId={editingSubtaskId}
            editingSubName={editingSubtaskName}
            onSubEditStart={handleSubtaskEditStart}
            onSubEditCancel={handleSubtaskEditCancel}
            onSubEditSubmit={handleSubtaskEditSubmit}
            deleteSubtask={deleteSubtask}
            setSelectedTaskGroup={handleSetSelectedTaskGroup}
            selectedTaskGroup={selectedTaskGroupId}
            selectedTask={selectedTask}
            selectedSubtask={selectedSubtask}
            setSelectedTask={handleSetSelectedTask}
            setSelectedSubtask={handleSetSelectedSubtask}
            handleTaskDateChange={handleTaskDateChange}
            handleSubtaskDateChange={handleSubtaskDateChange}
          />
        </div>
      </div>

      {showAddTaskGroupModal && (isOwner || taskAuthority) && (
        <AddTaskGroupModal
          onClose={() => setShowAddTaskGroupModal(false)}
          handleSubmit={addTaskGroup}
        />
      )}

      {/* Task Details Model*/}
      {selectedTaskGroup && (
        <TaskGroupDetailComponent
          taskGroup={selectedTaskGroup}
          onClose={() => setSelectedTaskGroup(null)}
          onAssignApprovers={handleAssignApprovers}
          onAddUpdate={handleAddTaskUpdate}
          refetchTaskGroups={() => statePersistentRefresh()}
        />
      )}
      {selectedTask && (
        <TaskDetailComponent
          task={selectedTask}
          refetchTasks={statePersistentRefresh}
          refetchSubtasks={fetchSubtasksForTask}
          onClose={() => setSelectedTask(null)}
          onAssignApprovers={handleAssignApprovers}
          onAddUpdate={handleAddTaskUpdate}
        />
      )}
      {selectedSubtask && (
        <div className="fixed inset-y-0 right-0 top-0 z-50 w-1/2">
          <SubtaskDetailComponent
            refetchSubtasks={fetchSubtasksForTask}
            subtask={selectedSubtask}
            taskId={selectedSubtask.task_id}
            onClose={() => setSelectedSubtask(null)}
          />
        </div>
      )}
    </div>
  );
};

export default TaskHome;